import { Component, computed, DestroyRef, effect, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { provideIcons } from '@ng-icons/core';
import { lucidePlus } from '@ng-icons/lucide';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { HlmSeparatorImports } from '@spartan-ng/helm/separator';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { ButtonAddNewComponent } from '../../../shared/components/button-add-new/button-add-new.component';
import { DialogConfirmDeleteComponent } from '../../../shared/components/dialog/confirm-delete/dialog-confirm-delete.component';
import { ConfirmDialogComponent } from '../../../shared/components/dialog/confirm-dialog/confirm-dialog.component';
import {
  ExpansionPanel,
  ExpansionPanelMenuAction,
} from '../../../shared/components/expansion-panel/expansion-panel.component';
import { SidepanelService } from '../../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../../shared/components/toastr/toastr-success.component';
import { TrierParPipe } from '../../../shared/pipes/trier-par.pipe';
import { FormCaracteristiqueComponent } from './form-caracteristique/form-caracteristique.component';
import { FormCaracteristiquesComponent } from './form-caracteristiques/form-caracteristiques.component';
import { FormSecteurActiviteComponent } from './form-secteur-activite/form-secteur-activite.component';
import { FormSecteursActiviteComponent } from './form-secteurs-activite/form-secteurs-activite.component';
import { FormSousCaracteristiqueComponent } from './form-sous-caracteristique/form-sous-caracteristique.component';
import { FormSousCaracteristiquesComponent } from './form-sous-caracteristiques/form-sous-caracteristiques.component';
import { Caractéristique } from './models/caractéristique.model';
import { CaractéristiqueStore } from './store/caractéristique.store';
import { Référentiel } from '../../../administration/gestion-référentiels/models/référentiel.model';

@Component({
  selector: 'app-perimetre-caracteristiques',
  imports: [
    HlmIconImports,
    HlmButtonImports,
    HlmItemImports,
    HlmSeparatorImports,
    ExpansionPanel,
    ButtonAddNewComponent,
    TrierParPipe,
    HlmDropdownMenuImports,
  ],
  templateUrl: './perimetre-caracteristiques.component.html',
  providers: [provideIcons({ lucidePlus })],
})
export class PerimetreCaracteristiquesComponent {
  readonly #caractéristiqueStore = inject(CaractéristiqueStore);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #dialog = inject(HlmDialogService);
  readonly #destroyRef = inject(DestroyRef);

  readonly referentiel = input.required<Référentiel>();

  readonly caracteristiques = computed(() =>
    this.#caractéristiqueStore.caracteristiques().filter((x) => !x.estSecteur),
  );

  readonly secteur = computed(() => this.#caractéristiqueStore.caracteristiques().find((x) => x.estSecteur));
  readonly peutModifier = input.required<boolean>();

  readonly secteursActiviteAction: ExpansionPanelMenuAction<Caractéristique>[] = [
    {
      icon: 'lucidePlus',
      label: "Ajouter un secteur d'activité",
      action: () => this.ajouterSecteur(),
    },
  ];

  readonly caracteristiquesAction: ExpansionPanelMenuAction<Caractéristique>[] = [
    {
      icon: 'lucidePlus',
      label: 'Ajouter une sous-caractéristique',
      action: (caracteristique: Caractéristique | undefined) => this.ajouterSousCaracteristique(caracteristique),
    },
    {
      icon: 'lucidePen',
      label: 'Modifier le nom',
      action: (caracteristique: Caractéristique | undefined) => this.#modifierNomCaractéristique(caracteristique),
    },
    {
      icon: 'lucideTrash',
      label: 'Supprimer la caractéristique',
      action: (caracteristique: Caractéristique | undefined) => this.#supprimerCaractéristique(caracteristique),
    },
  ];

  readonly sousCaracteristiqueAction: ExpansionPanelMenuAction<Caractéristique>[] = [
    {
      icon: 'lucidePen',
      label: 'Modifier la sous-caractéristique',
      action: (caracteristique: Caractéristique | undefined) => this.#modifierNomSousCaractéristique(caracteristique),
    },
    {
      icon: 'lucideTrash',
      label: 'Supprimer la sous-caractéristique',
      action: (caracteristique: Caractéristique | undefined) => this.#supprimerSousCaractéristique(caracteristique),
    },
  ];

  readonly secteurAction: ExpansionPanelMenuAction<Caractéristique>[] = [
    {
      icon: 'lucidePen',
      label: 'Modifier le nom',
      action: (secteur: Caractéristique | undefined) => this.#modifierNomSecteur(secteur),
    },
    {
      icon: 'lucideTrash',
      label: "Supprimer le secteur d'activité",
      action: (secteur: Caractéristique | undefined) => this.#supprimerSecteur(secteur),
    },
  ];

  constructor() {
    effect(() => this.#caractéristiqueStore.charger(this.referentiel().id));
  }

  ajouterSecteur() {
    this.#sidepanelService.setComponentToDisplay(
      "Ajouter une nouveau secteur d'activité",
      FormSecteursActiviteComponent,
      "Veuillez nommer le secteur d'activité que vous souhaitez créer.",
      { referentiel: this.referentiel(), secteur: this.secteur() },
    );
    this.#sidepanelService.open();
  }

  ajouterCaracteristique() {
    this.#sidepanelService.setComponentToDisplay(
      'Ajouter une nouvelle caractéristique',
      FormCaracteristiquesComponent,
      'Veuillez nommer la caractéristique que vous souhaitez créer.',
      { referentiel: this.referentiel() },
    );
    this.#sidepanelService.open();
  }

  ajouterSousCaracteristique(caracteristique?: Caractéristique) {
    if (!caracteristique) return;

    this.#sidepanelService.setComponentToDisplay(
      'Ajouter une nouvelle sous-caractéristique',
      FormSousCaracteristiquesComponent,
      `La caractéristique parente est ${caracteristique.nom}.`,
      { referentiel: this.referentiel(), caracteristique },
    );
    this.#sidepanelService.open();
  }

  #modifierNomSecteur(secteur?: Caractéristique) {
    if (!secteur) return;

    secteur = this.secteur()?.sousCaracteristiques.find((x) => x.id === secteur?.id);

    this.#sidepanelService.setComponentToDisplay(
      "Éditer un secteur d'activité",
      FormSecteurActiviteComponent,
      `Pour modifier le nom du secteur d'activité`,
      { referentiel: this.referentiel(), secteur },
    );
    this.#sidepanelService.open();
  }

  #modifierNomCaractéristique(caracteristique?: Caractéristique) {
    if (!caracteristique) return;

    this.#sidepanelService.setComponentToDisplay(
      'Éditer une caractéristique',
      FormCaracteristiqueComponent,
      `Pour modifier le nom de la caractéristique`,
      { caracteristique, referentiel: this.referentiel() },
    );
    this.#sidepanelService.open();
  }

  #modifierNomSousCaractéristique(caracteristique?: Caractéristique) {
    if (!caracteristique) return;

    const sousCaracteristique = this.caracteristiques()
      .find((x) => x.id === caracteristique?.idCaracteristiqueParente)
      ?.sousCaracteristiques.find((x) => x.id === caracteristique?.id);

    this.#sidepanelService.setComponentToDisplay(
      'Éditer une sous-caractéristique',
      FormSousCaracteristiqueComponent,
      `Vous pouvez modifier le parent et/ou le nom de la sous-caractéristique.`,
      { sousCaracteristique, referentiel: this.referentiel() },
    );
    this.#sidepanelService.open();
  }

  #supprimerSecteur(secteur?: Caractéristique) {
    if (!secteur) return;

    const toastrId = uuidv7();

    const dialogRef = this.#dialog.open(ConfirmDialogComponent, {
      context: {
        titre: `Supprimer le secteur d'activité`,
        contenu: `Êtes-vous sûr de vouloir supprimer le secteur d'activité ${secteur.nom} ?`,
        confirmBtnText: `Confirmer la suppression`,
      },
    });

    dialogRef.closed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((result) => {
      if (result) {
        this.#caractéristiqueStore.supprimer({
          idCaracteristique: secteur.id,
          next: () => {
            this.#caractéristiqueStore.charger(this.referentiel().id);
            toast.custom(ToastrSuccessComponent, {
              componentProps: {
                message: "Suppression du secteur d'activité réussie",
                id: toastrId,
              },
              id: toastrId,
            });
          },
        });
      }
    });
  }

  #supprimerCaractéristique(caracteristique?: Caractéristique) {
    if (!caracteristique) return;

    const nbSousCaractéristiques = caracteristique.sousCaracteristiques?.length ?? 0;
    const toastrId = uuidv7();

    let dialogRef;

    if (nbSousCaractéristiques) {
      dialogRef = this.#dialog.open(DialogConfirmDeleteComponent, {
        context: {
          nom: caracteristique.nom,
          childrenText: `${nbSousCaractéristiques} sous-caractéristiques liées`,
        },
      });
    } else {
      dialogRef = this.#dialog.open(ConfirmDialogComponent, {
        context: {
          titre: `Supprimer la caractéristique`,
          contenu: `Êtes-vous sûr de vouloir supprimer la caractéristique ${caracteristique.nom} ?`,
          confirmBtnText: `Confirmer la suppression`,
        },
      });
    }

    dialogRef.closed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((result) => {
      if (result) {
        this.#caractéristiqueStore.supprimer({
          idCaracteristique: caracteristique.id,
          next: () => {
            this.#caractéristiqueStore.charger(this.referentiel().id);
            toast.custom(ToastrSuccessComponent, {
              componentProps: {
                message: 'Suppression de la caractéristique réussie',
                id: toastrId,
              },
              id: toastrId,
            });
          },
        });
      }
    });
  }

  #supprimerSousCaractéristique(caracteristique?: Caractéristique) {
    if (!caracteristique) return;

    const toastrId = uuidv7();

    let dialogRef = this.#dialog.open(ConfirmDialogComponent, {
      context: {
        titre: `Supprimer la sous-caractéristique`,
        contenu: `Êtes-vous sûr de vouloir supprimer la sous-caractéristique ${caracteristique.nom} ?`,
        confirmBtnText: `Confirmer la suppression`,
      },
    });

    dialogRef.closed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((result) => {
      if (result) {
        this.#caractéristiqueStore.supprimer({
          idCaracteristique: caracteristique.id,
          next: () => {
            this.#caractéristiqueStore.charger(this.referentiel().id);
            toast.custom(ToastrSuccessComponent, {
              componentProps: {
                message: 'Suppression de la sous-caractéristique réussie',
                id: toastrId,
              },
              id: toastrId,
            });
          },
        });
      }
    });
  }
}
